// /catalog/products — lista admin de SaaS Products
// - Admin vê botões CRUD; comercial/financeiro/suporte só leitura
// - Filtros: status, type, integration_level, q
// - Dialog Radix pra criar + editar
// - Delete bloqueado se há subs ativas (backend 409); UI mostra fallback "Descontinuar"

import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertCircle,
  Boxes,
  ExternalLink,
  Pencil,
  Plus,
  Search,
  Settings2,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { CreateSaasProductDto, QuerySaasProductsDto } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { CatalogTabs } from '../../components/catalog/CatalogTabs';
import { PageHelp } from '../../components/common/PageHelp';
import { Field, Input, Select } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import { SaasProductForm } from '../../components/saas-products/SaasProductForm';
import {
  useCreateSaasProduct,
  useDeleteSaasProduct,
  useSaasProducts,
  useUpdateSaasProduct,
} from '../../hooks/use-saas-products';
import { useSetSaasAutoSuspension } from '../../hooks/use-suspension-policy';
import { extractErrorMessage } from '../../lib/errors';
import {
  diagnoseSaasConnection,
  isSaasConnected,
  type SaasProductEntity,
} from '../../lib/saas-products-api';
import { useAuthStore } from '../../stores/useAuthStore';

const STATUS_LABEL: Record<SaasProductEntity['status'], string> = {
  ativo: 'Ativo',
  em_desenvolvimento: 'Em desenvolvimento',
  pausado: 'Pausado',
  descontinuado: 'Descontinuado',
};

const STATUS_DOT: Record<SaasProductEntity['status'], string> = {
  ativo: 'bg-good ring-good/15',
  em_desenvolvimento: 'bg-warn ring-warn/15',
  pausado: 'bg-muted-2 ring-muted-3/15',
  descontinuado: 'bg-bad ring-bad/15',
};

const LEVEL_LABEL: Record<SaasProductEntity['integrationLevel'], string> = {
  manual: 'Manual',
  read_only: 'Read-only',
  full: 'Full',
};

export function SaasProductsPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | SaasProductEntity['status']>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SaasProductEntity | null>(null);

  const filters: QuerySaasProductsDto = useMemo(() => {
    const out: QuerySaasProductsDto = { limit: 50 };
    if (q.trim()) out.q = q.trim();
    if (statusFilter !== 'all') out.status = statusFilter;
    return out;
  }, [q, statusFilter]);

  const list = useSaasProducts(filters);
  const create = useCreateSaasProduct();
  const update = useUpdateSaasProduct();
  const remove = useDeleteSaasProduct();
  const setAutoSuspension = useSetSaasAutoSuspension();

  function openNew(): void {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(p: SaasProductEntity): void {
    setEditing(p);
    setFormOpen(true);
  }

  async function handleSubmit(values: CreateSaasProductDto): Promise<void> {
    try {
      if (editing) {
        const { slug: _slug, ...editable } = values;
        void _slug;
        await update.mutateAsync({
          id: editing.id,
          payload: { ...editable, version: editing.version },
        });
        toast.success('Produto atualizado.');
      } else {
        await create.mutateAsync(values);
        toast.success('Produto criado.');
      }
      setFormOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos salvar.'));
    }
  }

  async function handleToggleAutoSuspension(p: SaasProductEntity): Promise<void> {
    const enabling = !p.autoSuspensionEnabled;
    if (enabling) {
      const ok = window.confirm(
        `Ligar suspensão automática para ${p.name}? Clientes inadimplentes deste SaaS poderão ` +
          `ter o acesso bloqueado automaticamente quando a política global estiver ativa.`,
      );
      if (!ok) return;
    }
    try {
      const res = await setAutoSuspension.mutateAsync({
        saasProductId: p.id,
        enabled: enabling,
      });
      if (res.warning) {
        toast.warning('Suspensão automática ligada com ressalva.', { description: res.warning });
      } else {
        toast.success(
          enabling ? 'Suspensão automática ligada.' : 'Suspensão automática desligada.',
        );
      }
      await list.refetch();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos alterar a suspensão automática.'));
    }
  }

  async function handleDelete(p: SaasProductEntity): Promise<void> {
    const ok = window.confirm(
      `Descontinuar ${p.name}? Status muda pra "descontinuado" e mantém histórico. ` +
        `Se houver assinaturas ativas, a ação será bloqueada.`,
    );
    if (!ok) return;
    try {
      await remove.mutateAsync({ id: p.id, version: p.version });
      toast.success('Produto descontinuado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos descontinuar o produto.'));
    }
  }

  return (
    <div className="space-y-5">
      <CatalogTabs />

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">
              Produtos SaaS
            </h1>
            <p className="mt-1 text-[13px] text-muted">
              Catálogo dos SaaS Owl conectados ao Control Center.
            </p>
          </div>
          <PageHelp id="saas-products" />
        </div>
        {isAdmin && (
          <PillButton solid icon={<Plus size={14} strokeWidth={2} />} onClick={openNew}>
            Novo produto
          </PillButton>
        )}
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Field label="Buscar">
            {({ inputId }) => (
              <div className="relative">
                <Search
                  size={14}
                  strokeWidth={2}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
                />
                <Input
                  id={inputId}
                  type="text"
                  placeholder="Nome ou slug"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </Field>
        </div>
        <div className="min-w-[180px]">
          <Field label="Status">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as 'all' | SaasProductEntity['status'])
                }
              >
                <option value="all">Todos</option>
                {(['ativo', 'em_desenvolvimento', 'pausado', 'descontinuado'] as const).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </div>

      {/* Lista */}
      {list.isLoading ? (
        <SkeletonGrid />
      ) : list.isError ? (
        <ErrorBox
          message={extractErrorMessage(list.error, 'Não conseguimos carregar.')}
          onRetry={() => void list.refetch()}
        />
      ) : list.data && list.data.items.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {list.data.items.map((p) => (
            <article
              key={p.id}
              className="flex flex-col gap-3 rounded-[14px] border border-line bg-panel p-4 transition-colors hover:border-line-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-3">
                  <span
                    aria-hidden
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-panel-3 text-muted"
                  >
                    <Boxes size={16} strokeWidth={1.8} />
                  </span>
                  <div className="min-w-0">
                    <h2 className="m-0 truncate text-[15px] font-semibold text-ink">{p.name}</h2>
                    <code className="mt-[2px] block truncate font-mono text-[11px] text-muted-2">
                      {p.slug}
                    </code>
                  </div>
                </div>
                <div className="flex items-center gap-[10px]">
                  <span
                    className={cn(
                      'inline-flex items-center gap-[6px] text-[11px] font-medium',
                      p.status === 'ativo' ? 'text-ink-2' : 'text-muted',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'h-[7px] w-[7px] rounded-full ring-[3px]',
                        STATUS_DOT[p.status],
                      )}
                    />
                    {STATUS_LABEL[p.status]}
                  </span>
                  {(() => {
                    const connected = isSaasConnected(p);
                    const diagnosis = diagnoseSaasConnection(p);
                    let tooltip = '';
                    switch (diagnosis.status) {
                      case 'connected':
                        tooltip = `Saudável · respondeu em ${p.lastHealthCheck?.responseTimeMs ?? '?'}ms`;
                        break;
                      case 'no_config':
                        tooltip = diagnosis.reasons.join(' · ');
                        break;
                      case 'never_checked':
                        tooltip = 'Aguardando primeiro health check (cron 5min)';
                        break;
                      case 'unhealthy':
                        tooltip = `SaaS respondeu com erro: ${diagnosis.errorMessage ?? `HTTP ${diagnosis.httpStatus}`}`;
                        break;
                      case 'stale': {
                        const min = Math.round(diagnosis.ageMs / 60000);
                        tooltip = `Sem resposta há ${min}min — verifique se o SaaS está no ar`;
                        break;
                      }
                    }
                    return (
                      <span
                        className={cn(
                          'inline-flex items-center gap-[6px] text-[11px] font-medium',
                          connected ? 'text-ink-2' : 'text-muted',
                        )}
                        title={tooltip}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'h-[7px] w-[7px] rounded-full ring-[3px]',
                            connected ? 'bg-good ring-good/15' : 'bg-muted-2 ring-muted-3/15',
                          )}
                        />
                        {connected ? 'Conectado' : 'Desconectado'}
                      </span>
                    );
                  })()}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-2">
                <span className="inline-flex items-center gap-1">
                  <Settings2 size={11} strokeWidth={1.8} />
                  {LEVEL_LABEL[p.integrationLevel]}
                </span>
                <span>·</span>
                <span>{p.environment}</span>
                <span>·</span>
                <span className="tnum">{p.rateLimitPerMin}/min</span>
              </div>

              {p.descriptionShort && (
                <p className="line-clamp-2 text-[12px] text-muted">{p.descriptionShort}</p>
              )}

              {isAdmin && (
                <div className="flex items-center justify-between gap-2 rounded-[10px] border border-line bg-panel-2 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium text-ink-2">Suspensão automática</div>
                    {p.autoSuspensionEnabled && p.integrationLevel !== 'full' ? (
                      <div className="mt-[2px] flex items-center gap-1 text-[10px] text-warn">
                        <AlertCircle size={10} strokeWidth={2} />
                        Sem integração full — suspensões irão pra DLQ.
                      </div>
                    ) : (
                      <div className="mt-[2px] text-[10px] text-muted-2">
                        {p.autoSuspensionEnabled ? 'Ligada para este SaaS' : 'Desligada'}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={p.autoSuspensionEnabled}
                    aria-label={`Suspensão automática de ${p.name}`}
                    disabled={setAutoSuspension.isPending}
                    onClick={() => void handleToggleAutoSuspension(p)}
                    className={cn(
                      'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50',
                      p.autoSuspensionEnabled ? 'bg-ink' : 'bg-line-2',
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block h-4 w-4 transform rounded-full bg-panel transition-transform',
                        p.autoSuspensionEnabled ? 'translate-x-[18px]' : 'translate-x-[2px]',
                      )}
                    />
                  </button>
                </div>
              )}

              <div className="mt-auto flex items-center gap-2 border-t border-line pt-3">
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-muted transition-colors hover:text-ink"
                  >
                    Abrir
                    <ExternalLink size={11} strokeWidth={1.8} />
                  </a>
                )}
                <div className="ml-auto flex items-center gap-1">
                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        aria-label={`Editar ${p.name}`}
                        onClick={() => openEdit(p)}
                        className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink"
                      >
                        <Pencil size={13} strokeWidth={1.8} />
                      </button>
                      <button
                        type="button"
                        aria-label={`Descontinuar ${p.name}`}
                        onClick={() => void handleDelete(p)}
                        disabled={p.status === 'descontinuado'}
                        className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-bad disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Trash2 size={13} strokeWidth={1.8} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          isAdmin={isAdmin}
          hasFilters={Boolean(q || statusFilter !== 'all')}
          onAdd={openNew}
        />
      )}

      {/* Dialog: criar / editar */}
      {isAdmin && (
        <Dialog.Root open={formOpen} onOpenChange={setFormOpen}>
          <Dialog.Portal>
            <Dialog.Overlay
              className={cn(
                'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              )}
            />
            <Dialog.Content
              className={cn(
                'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
                'w-[min(720px,calc(100vw-32px))] max-h-[calc(100vh-48px)] overflow-y-auto',
                'rounded-[14px] border border-line bg-panel shadow-lg p-7',
                'focus:outline-none',
              )}
            >
              <div className="mb-5 flex items-start justify-between gap-4">
                <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
                  {editing ? `Editar ${editing.name}` : 'Novo produto SaaS'}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Fechar"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
                  >
                    <X size={15} strokeWidth={2} />
                  </button>
                </Dialog.Close>
              </div>

              <SaasProductForm
                editMode={Boolean(editing)}
                hasBootstrapKey={editing?.hasBootstrapApiKey}
                hasWebhookSecret={editing?.hasWebhookSecret}
                defaultValues={editing ? toFormDefaults(editing) : undefined}
                submitLabel={editing ? 'Salvar alterações' : 'Criar produto'}
                isPending={create.isPending || update.isPending}
                onSubmit={handleSubmit}
                onCancel={() => setFormOpen(false)}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function SkeletonGrid(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          aria-hidden
          className="h-[140px] animate-pulse rounded-[14px] border border-line bg-panel-2"
        />
      ))}
    </div>
  );
}

function ErrorBox({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/30 bg-bad/5 p-5 text-[13px]">
      <div className="flex items-center gap-2 font-medium text-bad">
        <AlertCircle size={14} strokeWidth={2} aria-hidden />
        Não conseguimos carregar os produtos.
      </div>
      <p className="mt-1 text-muted">{message}</p>
      <div className="mt-3">
        <PillButton onClick={onRetry}>Tentar de novo</PillButton>
      </div>
    </div>
  );
}

function EmptyState({
  isAdmin,
  hasFilters,
  onAdd,
}: {
  isAdmin: boolean;
  hasFilters: boolean;
  onAdd: () => void;
}): JSX.Element {
  if (hasFilters) {
    return (
      <div className="rounded-[14px] border border-line bg-panel-2 p-6 text-center">
        <p className="text-[13px] text-muted">Nenhum produto bate com os filtros.</p>
      </div>
    );
  }
  return (
    <div className="rounded-[14px] border border-line bg-panel-2 p-8 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-panel-3 text-muted-2">
        <Boxes size={18} strokeWidth={1.5} />
      </div>
      <p className="text-[14px] font-medium text-ink-2">Sem produtos cadastrados ainda.</p>
      <p className="mt-1 text-[12px] text-muted">
        Cadastre Reservei, OwlTask, Vizzy AI e outros SaaS do ecossistema.
      </p>
      {isAdmin && (
        <div className="mt-4 flex justify-center">
          <PillButton solid icon={<Plus size={14} strokeWidth={2} />} onClick={onAdd}>
            Cadastrar primeiro produto
          </PillButton>
        </div>
      )}
    </div>
  );
}

/**
 * Mapeia o produto salvo de volta pros campos do formulario.
 *
 * ATENCAO ao adicionar campo novo: esta funcao lista campo por campo. Esquecer
 * um aqui nao da erro de tipo (o retorno e Partial) — o formulario so reabre com
 * ele vazio, e o proximo \salvar\ APAGA o valor que estava no banco. Foi o que
 * aconteceu com  em 2026-08-20. Exportada pra ser testavel.
 */
export function toFormDefaults(p: SaasProductEntity): Partial<CreateSaasProductDto> {
  return {
    slug: p.slug,
    name: p.name,
    description_short: p.descriptionShort ?? '',
    description_long: p.descriptionLong ?? '',
    type: p.type,
    status: p.status,
    url: p.url ?? '',
    admin_url: p.adminUrl ?? '',
    email_logo_url: p.emailLogoUrl ?? '',
    subscription_url: p.subscriptionUrl ?? '',
    // Sem esta linha o formulario reabre com o campo VAZIO e, ao salvar
    // qualquer outra alteracao, manda string vazia e APAGA a logo do produto.

    environment: p.environment,
    technical_user_id: p.technicalUserId,
    commercial_user_id: p.commercialUserId,
    observations: p.observations ?? '',
    integration_level: p.integrationLevel,
    // Sem esta linha o formulário reabre no padrão "e-mail" e, ao salvar
    // qualquer outra coisa, tira o Reservei do modo "login do sistema".
    identity_mode: p.identityMode,
    asaas_account_id: p.asaasAccountId,
    api_url: p.apiUrl ?? '',
    bootstrap_api_key: '',
    webhook_secret: '',
    rate_limit_per_min: p.rateLimitPerMin,
  };
}
